import { WEBXR_TO_NUE } from "../webxr-nue-basis.js";
import { AxesHelper, BoxGeometry, Group, Mesh, MeshBasicMaterial } from "three";
//#region ../src/ar/qr/qr-debug-view.ts
/**
* QR debug view — the shared 3D "is it glued to the code?" overlay: a 3D axis at
* the solved QR pose plus a semi-transparent cube sized to the QR so its front
* face lands on the printed corners. Both are parented under `arWorldGroup` so
* they ride the alignment / transform chain exactly like real content.
*
* Promoted to the framework (recorder live-QR WS-5) as the **shared consumer**
* view: both the QR-tracking demo and the GPS Recorder render this same overlay
* off the derived pose (`qr-derived-pose.ts` / `selectSolvedQrPose`). The view is
* pure three.js — it knows nothing about who solved the pose, so it runs
* identically live and on replay.
*
* Persistence across detection misses: `update()` is called on every lock;
* `clear()` is NOT called on a miss, so the objects keep their last pose and
* never flicker between throttled detections. They stay hidden until the first
* `update()`.
*
* Coordinate space (IMPORTANT): the QR pose is in **raw WebXR** space (the
* corners are depth-unprojected with the raw WebXR camera pose), but the
* injected `parent` (`arWorldGroup`) local space is **NUE**. So the objects must
* ride the SAME {@link WEBXR_TO_NUE} basis the camera does (`arWorldGroup →
* basisChangeNode → arpose → camera`). We mirror that by hanging them off an
* internal basis node carrying `WEBXR_TO_NUE`; parenting them directly under
* `arWorldGroup` would leave them East/North axis-swapped and they would NOT
* line up with the camera/QR on a real device (the recurring scene-frame bug —
* see the frame-tile / occupancy-cube / hit-test-reticle precedents). We never
* touch the camera; WebXR owns its pose.
*
* @see qr-derived-pose.ts — derives the `Pose` this view renders (size + PnP).
* @see webxr-nue-basis.ts — the `WEBXR_TO_NUE` basis the objects ride.
*/
/** A thin slab so the cube's front face sits on the printed code (1 cm deep). */
const CUBE_DEPTH_M = .01;
/**
* Create the debug axis + cube under `parent` (the `arWorldGroup`). Objects
* start hidden; the first `update()` reveals and positions them.
*/
function createQrDebugView(parent) {
	const basis = new Group();
	basis.name = "qr-debug-basis";
	basis.matrix.copy(WEBXR_TO_NUE);
	basis.matrixAutoUpdate = false;
	parent.add(basis);
	const axes = new AxesHelper(.15);
	axes.visible = false;
	const cubeMaterial = new MeshBasicMaterial({
		color: 3399167,
		transparent: true,
		opacity: .35,
		depthWrite: false
	});
	const cube = new Mesh(new BoxGeometry(1, 1, 1), cubeMaterial);
	cube.visible = false;
	basis.add(axes);
	basis.add(cube);
	function applyPose(object, pose) {
		object.position.set(pose.position[0], pose.position[1], pose.position[2]);
		object.quaternion.set(pose.rotation[0], pose.rotation[1], pose.rotation[2], pose.rotation[3]);
	}
	return {
		update(pose, sizeM) {
			applyPose(axes, pose);
			axes.visible = true;
			if (sizeM === null) {
				cube.visible = false;
				return;
			}
			applyPose(cube, pose);
			cube.scale.set(sizeM, sizeM, CUBE_DEPTH_M);
			cube.translateZ(-CUBE_DEPTH_M / 2);
			cube.visible = true;
		},
		clear() {
			axes.visible = false;
			cube.visible = false;
		},
		dispose() {
			parent.remove(basis);
			axes.dispose();
			cube.geometry.dispose();
			cubeMaterial.dispose();
		}
	};
}
//#endregion
export { createQrDebugView };
